import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Checkbox, FormControlLabel, makeStyles } from '@material-ui/core'
import { DndProvider, useDrag, useDrop } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import clsx from 'clsx'
import SydModal from '../../commonDesign/SydModal'
import SydModalActions from '../../commonDesign/SydModal/SydModalActions'
import SydButton from '../../commonDesign/Button'
import SydSelect from '../../commonDesign/SydSelect'
import Icon from '../../atoms/Icon'
import { CALCULATION_TYPES, DATE_MODE_OPTIONS, createColumnInstance } from './lib/constants'

const DRAG_TYPE = 'COLUMN_ITEM'
const CALC_TYPE_LIST = Object.values(CALCULATION_TYPES).sort((a, b) => a.label.localeCompare(b.label))

const useStyles = makeStyles(() => ({
  body: {
    display: 'flex',
    gap: 16,
    minHeight: 420
  },
  catalogPanel: {
    flex: '0 0 190px',
    display: 'flex',
    flexDirection: 'column',
    borderRight: '1px solid var(--opal-border-default, #e0e0e0)',
    paddingRight: 16
  },
  columnPanel: {
    flex: '1 1 auto',
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0
  },
  detailPanel: {
    flex: '0 0 300px',
    display: 'flex',
    flexDirection: 'column',
    borderLeft: '1px solid var(--opal-border-default, #e0e0e0)',
    paddingLeft: 16
  },
  panelHeader: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: 'var(--color-neutral-600, #666)',
    marginBottom: 8
  },
  optionList: {
    flex: 1,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 2
  },
  optionLabel: {
    '& .MuiFormControlLabel-label': {
      fontSize: 13
    }
  },
  addLink: {
    fontSize: 11,
    color: 'var(--color-primary-500, #1976d2)',
    cursor: 'pointer',
    marginLeft: 32,
    marginTop: -2,
    marginBottom: 4,
    '&:hover': { textDecoration: 'underline' }
  },
  columnList: {
    flex: 1,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    border: '1px solid var(--opal-border-default, #e0e0e0)',
    borderRadius: 'var(--border-radius-sm, 4px)',
    padding: 4,
    minHeight: 100
  },
  columnRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '7px 8px',
    borderRadius: 'var(--border-radius-sm, 4px)',
    fontSize: 13,
    cursor: 'grab',
    userSelect: 'none',
    '&:hover': {
      background: 'var(--color-neutral-100, #f0f0f0)'
    }
  },
  columnRowSelected: {
    background: 'var(--color-primary-50, rgba(25, 118, 210, 0.08))',
    outline: '1px solid var(--color-primary-200, #90caf9)',
    '&:hover': {
      background: 'var(--color-primary-50, rgba(25, 118, 210, 0.08))'
    }
  },
  columnRowDragging: {
    opacity: 0.4
  },
  dragHandle: {
    color: 'var(--color-neutral-400, #aaa)',
    flexShrink: 0
  },
  columnLabel: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontWeight: 500
  },
  columnDateHint: {
    fontSize: 11,
    color: 'var(--color-neutral-500, #888)',
    flexShrink: 0
  },
  removeBtn: {
    cursor: 'pointer',
    color: 'var(--color-neutral-400, #aaa)',
    flexShrink: 0,
    '&:hover': {
      color: 'var(--color-danger-500, #d32f2f)'
    }
  },
  emptyHint: {
    color: 'var(--color-neutral-400, #aaa)',
    fontSize: 12,
    fontStyle: 'italic',
    padding: 8,
    textAlign: 'center'
  },
  detailTitle: {
    fontSize: 15,
    fontWeight: 600,
    marginBottom: 16,
    color: 'var(--color-neutral-800, #333)'
  },
  detailSection: {
    marginBottom: 16
  },
  detailSectionLabel: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: 'var(--color-neutral-500, #888)',
    marginBottom: 6
  },
  detailField: {
    marginBottom: 12
  },
  detailFieldLabel: {
    fontSize: 12,
    color: 'var(--color-neutral-600, #666)',
    marginBottom: 4
  },
  dateRangeRow: {
    display: 'flex',
    gap: 8
  },
  dateInput: {
    flex: 1,
    padding: '6px 8px',
    fontSize: 13,
    border: 'var(--border-neutral-thin, 1px solid #ddd)',
    borderRadius: 'var(--border-radius-sm, 4px)',
    background: 'var(--theme-color-surface-d0, #fff)',
    color: 'inherit',
    outline: 'none',
    '&:focus': {
      outline: '1px solid var(--color-primary-500, #1976d2)'
    }
  },
  settingCheckLabel: {
    '& .MuiFormControlLabel-label': {
      fontSize: 13
    }
  }
}))

function getDateHint (options) {
  if (!options?.dateMode || options.dateMode === 'default') return null
  if (options.dateMode === 'quickDate' && options.quickDate) return options.quickDate
  if (options.dateMode === 'dateRange') return 'Custom'
  return null
}

function parseDateModeValue (value) {
  if (!value || value === 'default') return { dateMode: 'default', quickDate: null }
  if (value === 'dateRange') return { dateMode: 'dateRange', quickDate: null }
  if (value.startsWith('qd:')) return { dateMode: 'quickDate', quickDate: value.slice(3) }
  return { dateMode: 'default', quickDate: null }
}

function toDateModeSelectValue (options) {
  if (!options?.dateMode || options.dateMode === 'default') return 'default'
  if (options.dateMode === 'dateRange') return 'dateRange'
  if (options.dateMode === 'quickDate' && options.quickDate) return `qd:${options.quickDate}`
  return 'default'
}

function DraggableColumnRow ({ column, index, isSelected, moveItem, onSelect, onRemove }) {
  const classes = useStyles()
  const ref = useRef(null)
  const typeDef = CALCULATION_TYPES[column.type]
  const dateHint = getDateHint(column.options)

  const [{ isDragging }, drag] = useDrag({
    type: DRAG_TYPE,
    item: () => ({ index }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  })

  const [, drop] = useDrop({
    accept: DRAG_TYPE,
    hover (item, monitor) {
      if (!ref.current) return
      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) return

      const hoverBoundingRect = ref.current.getBoundingClientRect()
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const clientOffset = monitor.getClientOffset()
      const hoverClientY = clientOffset.y - hoverBoundingRect.top

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return

      moveItem(dragIndex, hoverIndex)
      item.index = hoverIndex
    }
  })

  drag(drop(ref))

  return (
    <div
      ref={ref}
      className={clsx(classes.columnRow, {
        [classes.columnRowSelected]: isSelected,
        [classes.columnRowDragging]: isDragging
      })}
      onClick={() => onSelect(column.id)}
    >
      <Icon name='dragIndicator' customSize={14} additionalClasses={classes.dragHandle} />
      <span className={classes.columnLabel}>{typeDef?.label || column.type}</span>
      {dateHint && <span className={classes.columnDateHint}>{dateHint}</span>}
      <span
        className={classes.removeBtn}
        onClick={(e) => { e.stopPropagation(); onRemove(column.id) }}
      >
        <Icon name='close' customSize={14} />
      </span>
    </div>
  )
}

DraggableColumnRow.propTypes = {
  column: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  isSelected: PropTypes.bool,
  moveItem: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired
}

function ColumnDetailPanel ({ column, onOptionChange }) {
  const classes = useStyles()

  if (!column) {
    return (
      <div className={classes.detailPanel}>
        <div className={classes.panelHeader}>Settings</div>
        <div className={classes.emptyHint}>Select a column to configure</div>
      </div>
    )
  }

  const typeDef = CALCULATION_TYPES[column.type]
  const supportsDate = typeDef?.supportsDate
  const settingsSchema = typeDef?.settingsSchema || []
  const isCustomRange = column.options?.dateMode === 'dateRange'

  const handleDateModeChange = (e) => {
    const { dateMode, quickDate } = parseDateModeValue(e.target.value)
    onOptionChange(column.id, {
      dateMode,
      quickDate,
      startDate: null,
      endDate: null
    })
  }

  return (
    <div className={classes.detailPanel}>
      <div className={classes.detailTitle}>{typeDef?.label || column.type}</div>

      {supportsDate && (
        <div className={classes.detailSection}>
          <div className={classes.detailSectionLabel}>Date Range</div>
          <div className={classes.detailField}>
            <SydSelect
              options={DATE_MODE_OPTIONS}
              value={toDateModeSelectValue(column.options)}
              onChange={handleDateModeChange}
              size='sm'
              minWidth='200px'
            />
          </div>
          {isCustomRange && (
            <div className={classes.dateRangeRow}>
              <input
                type='date'
                className={classes.dateInput}
                value={column.options?.startDate || ''}
                onChange={(e) => onOptionChange(column.id, { startDate: e.target.value || null })}
              />
              <input
                type='date'
                className={classes.dateInput}
                value={column.options?.endDate || ''}
                onChange={(e) => onOptionChange(column.id, { endDate: e.target.value || null })}
              />
            </div>
          )}
        </div>
      )}

      {settingsSchema.length > 0 && (
        <div className={classes.detailSection}>
          <div className={classes.detailSectionLabel}>Options</div>
          {settingsSchema.map(setting => {
            if (setting.control === 'select') {
              return (
                <div key={setting.key} className={classes.detailField}>
                  <div className={classes.detailFieldLabel}>{setting.label}</div>
                  <SydSelect
                    options={setting.options}
                    value={column.options?.[setting.key] ?? setting.defaultValue}
                    onChange={(e) => onOptionChange(column.id, { [setting.key]: e.target.value })}
                    size='sm'
                    minWidth='200px'
                  />
                </div>
              )
            }
            if (setting.control === 'checkbox') {
              return (
                <FormControlLabel
                  key={setting.key}
                  className={classes.settingCheckLabel}
                  control={
                    <Checkbox
                      checked={column.options?.[setting.key] ?? setting.defaultValue}
                      onChange={(e) => onOptionChange(column.id, { [setting.key]: e.target.checked })}
                      size='small'
                      color='primary'
                    />
                  }
                  label={setting.label}
                />
              )
            }
            return null
          })}
        </div>
      )}

      {!supportsDate && settingsSchema.length === 0 && (
        <div className={classes.emptyHint}>No configurable options for this type</div>
      )}
    </div>
  )
}

ColumnDetailPanel.propTypes = {
  column: PropTypes.object,
  onOptionChange: PropTypes.func.isRequired
}

export default function ColumnBuilderDialog ({ open, onClose, currentColumns, onApply }) {
  const classes = useStyles()
  const [columns, setColumns] = useState(currentColumns || [])
  const [selectedColumnId, setSelectedColumnId] = useState(null)

  useEffect(() => {
    if (open) {
      setColumns(currentColumns || [])
      setSelectedColumnId(null)
    }
  }, [open, currentColumns])

  const typesInUse = new Set(columns.map(c => c.type))
  const selectedColumn = columns.find(c => c.id === selectedColumnId) || null

  const handleTypeToggle = useCallback((type) => {
    setColumns(prev => {
      const hasType = prev.some(c => c.type === type)
      if (hasType) {
        const removedIds = new Set(prev.filter(c => c.type === type).map(c => c.id))
        setSelectedColumnId(cur => removedIds.has(cur) ? null : cur)
        return prev.filter(c => c.type !== type)
      }
      const instance = createColumnInstance(type)
      if (instance) {
        setSelectedColumnId(instance.id)
        return [...prev, instance]
      }
      return prev
    })
  }, [])

  const handleAddAnother = useCallback((type) => {
    const instance = createColumnInstance(type)
    if (instance) {
      setColumns(prev => [...prev, instance])
      setSelectedColumnId(instance.id)
    }
  }, [])

  const handleRemove = useCallback((id) => {
    setColumns(prev => prev.filter(c => c.id !== id))
    setSelectedColumnId(cur => cur === id ? null : cur)
  }, [])

  const handleOptionChange = useCallback((id, optionPatch) => {
    setColumns(prev => prev.map(c =>
      c.id === id ? { ...c, options: { ...c.options, ...optionPatch } } : c
    ))
  }, [])

  const moveItem = useCallback((fromIndex, toIndex) => {
    setColumns(prev => {
      const next = [...prev]
      const [moved] = next.splice(fromIndex, 1)
      next.splice(toIndex, 0, moved)
      return next
    })
  }, [])

  const handleApply = useCallback(() => {
    onApply(columns)
    onClose()
  }, [columns, onApply, onClose])

  return (
    <SydModal open={open} onClose={onClose} title='Column Builder' size='large'>
      <DndProvider backend={HTML5Backend}>
        <div className={classes.body}>
          <div className={classes.catalogPanel}>
            <div className={classes.panelHeader}>Calculation Types</div>
            <div className={classes.optionList}>
              {CALC_TYPE_LIST.map(typeDef => (
                <React.Fragment key={typeDef.type}>
                  <FormControlLabel
                    className={classes.optionLabel}
                    control={
                      <Checkbox
                        checked={typesInUse.has(typeDef.type)}
                        onChange={() => handleTypeToggle(typeDef.type)}
                        size='small'
                        color='primary'
                      />
                    }
                    label={typeDef.label}
                  />
                  {typesInUse.has(typeDef.type) && (
                    <div
                      className={classes.addLink}
                      onClick={() => handleAddAnother(typeDef.type)}
                    >
                      + Add another
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className={classes.columnPanel}>
            <div className={classes.panelHeader}>Columns (drag to reorder)</div>
            <div className={classes.columnList}>
              {columns.length === 0 && (
                <div className={classes.emptyHint}>Select calculation types from the left</div>
              )}
              {columns.map((col, index) => (
                <DraggableColumnRow
                  key={col.id}
                  column={col}
                  index={index}
                  isSelected={col.id === selectedColumnId}
                  moveItem={moveItem}
                  onSelect={setSelectedColumnId}
                  onRemove={handleRemove}
                />
              ))}
            </div>
          </div>

          <ColumnDetailPanel
            column={selectedColumn}
            onOptionChange={handleOptionChange}
          />
        </div>
      </DndProvider>
      <SydModalActions>
        <SydButton variant='outline' size='sm' onClick={onClose}>Cancel</SydButton>
        <SydButton variant='primary' size='sm' onClick={handleApply} disabled={columns.length === 0}>Apply</SydButton>
      </SydModalActions>
    </SydModal>
  )
}

ColumnBuilderDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentColumns: PropTypes.arrayOf(PropTypes.object),
  onApply: PropTypes.func.isRequired
}
